import dayjs from 'dayjs';
import { useEffect } from 'react';
import { type Control, useWatch } from 'react-hook-form';

import type { Order } from '~hooks/useOrders/models';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Triggers: `loadAt` (watched); the mount-time edit/create distinction.
 * Writes:   the Provider's `showPastDateWarning` state, rendered by
 *           DateAndTimePanel as an error alert. Never a form field — the
 *           warning is advisory and a past start date stays submittable.
 * Guards:   editing a persisted order (or an empty `loadAt`) always clears
 *           the warning; only new orders compare against today.
 * Legacy:   the legacy form's past-loadAt warning.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("past loadAt warning").
 */
export function usePastLoadAtWarning({
  control,
  defaultOrder,
  setShowPastDateWarning,
}: {
  control: Control<OrderFormV3FieldValues>;
  defaultOrder: Order | null;
  setShowPastDateWarning: (show: boolean) => void;
}): void {
  const loadAt = useWatch({ control, name: 'loadAt' });
  const isEditingOrder = Boolean(defaultOrder?.id);

  useEffect(() => {
    if (isEditingOrder || !loadAt) {
      setShowPastDateWarning(false);
      return;
    }

    const today = dayjs.tz().startOf('day');
    const selectedDate = dayjs.tz(loadAt).startOf('day');
    setShowPastDateWarning(selectedDate.isBefore(today));
    // Legacy deps verbatim ([isEditingOrder, watchedLoadAt]) — the setter is
    // read via closure, exactly as legacy reads its useState setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingOrder, loadAt]);
}
